import CryptoJS from "crypto-js";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAuthReducerState, IGetFrameImageResponseData, ILoginRequestData, ILoginUserDetails } from "../../interfaces";
import authServices from "../services/auth.services";
import { minBy } from "lodash";

const encryptionKey = String(process.env.REACT_APP_ENCRYPTION_KEY);

const initialState: IAuthReducerState = {
	loading: false,
	success: false,
	error: "",
	currentAction: "",
	unauthorizedError: false,
	user: {
		token: "",
		username: "",
		role: 0
	},
	cachedImagesMap: {}
};

export const loginUser = createAsyncThunk<ILoginUserDetails, ILoginRequestData>(
	"auth/loginUser",
	authServices.handleLoginUser
);

export const logoutUser = createAsyncThunk<void>("auth/logoutUser", authServices.handleLogoutUser);

const authReducer = createSlice({
	name: "auth",
	initialState: initialState,
	reducers: {
		clearUnauthState: (state) => {
			state.unauthorizedError = false;
			state.user.token = "";
		},

		clearAuthState: (state) => {
			state.unauthorizedError = false;
			state.user = {
				username: "",
				token: "",
				role: 0
			};
		},

		updateToken: (state, { payload }: PayloadAction<string>) => {
			state.user.token = CryptoJS.AES.encrypt(payload, encryptionKey).toString();
		},

		addCachedImageData: (
			state,
			{ payload }: PayloadAction<{ key: string; data: IGetFrameImageResponseData; timestamp: number }>
		) => {
			const { key, data, timestamp } = payload;

			const updatedCachedImagesMap = { ...state.cachedImagesMap };
			updatedCachedImagesMap[key] = { data, timestamp };

			const plateImages = Object.entries(updatedCachedImagesMap)
				.filter(([key]) => key.startsWith("plate"))
				.map(([key, value]) => ({
					...value,
					key
				}));

			if (plateImages.length > 300) {
				const oldestPlateImage = minBy(plateImages, "timestamp");
				if (oldestPlateImage) delete updatedCachedImagesMap[oldestPlateImage.key];
			}

			const frameImages = Object.entries(updatedCachedImagesMap)
				.filter(([key]) => key.startsWith("frame"))
				.map(([key, value]) => ({
					...value,
					key
				}));

			if (frameImages.length > 100) {
				const oldestFrameImage = minBy(frameImages, "timestamp");
				if (oldestFrameImage) delete updatedCachedImagesMap[oldestFrameImage.key];
			}

			state.cachedImagesMap = updatedCachedImagesMap;
		},

		updateCachedImageTimestamp: (state, { payload }: PayloadAction<{ key: string; timestamp: number }>) => {
			const { key, timestamp } = payload;
			state.cachedImagesMap[key].timestamp = timestamp;
		}
	},
	extraReducers: (builder) => {
		// LOGIN USER
		builder.addCase(loginUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
		});

		builder.addCase(loginUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.user = {
				token: "",
				username: "",
				role: 0
			};
		});

		builder.addCase(loginUser.fulfilled, (state, { payload }) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/loginUser";
			state.unauthorizedError = false;
			state.user = { ...payload, token: CryptoJS.AES.encrypt(payload.token, encryptionKey).toString() };
		});

		// LOGOUT USER
		builder.addCase(logoutUser.pending, (state) => {
			state.loading = true;
			state.success = false;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
		});

		builder.addCase(logoutUser.rejected, (state, { error }) => {
			state.loading = false;
			state.success = false;
			state.error = error.message ?? "Something went wrong";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
		});

		builder.addCase(logoutUser.fulfilled, (state) => {
			state.loading = false;
			state.success = true;
			state.error = "";
			state.currentAction = "auth/logoutUser";
			state.unauthorizedError = false;
			state.user = {
				token: "",
				username: "",
				role: 0
			};
		});
	}
});

export const { clearUnauthState, clearAuthState, updateToken, addCachedImageData, updateCachedImageTimestamp } =
	authReducer.actions;

export default authReducer;
